import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Typography } from "antd";
import SectionCard from "../shared/SectionCard";
import HiServiceModeNotice from "./HiServiceModeNotice";
import { requestAddressIhiSearch, requestCoreDemographicIhiRevalidation } from "../../api/healthcare/ihiRevalidationApi";
import {
	CoreDemographicRevalidationOffer,
	RevalidationFeedback,
	UNREADABLE_CHANGED_RECORD_FEEDBACK,
	describeChangedDemographics,
	describeCoreDemographicRevalidationError,
	describeCoreDemographicRevalidationResult,
	parseCoreDemographicRevalidationResult,
	responseReportsPatientRecordChanged,
} from "../../utils/healthcare/CoreDemographicIhiRevalidation";
import { describeAddressIhiSearchError, describeAddressIhiSearchResult, parseAddressIhiSearchResult } from "../../utils/healthcare/AddressIhiSearch";
import type { UserUuid } from "../../types/identity";

const { Text } = Typography;

interface IhiRevalidationPanelProps {
	patientId: UserUuid;
	/** Present only after this session saved a family-name, sex or date-of-birth change. */
	offer: CoreDemographicRevalidationOffer | null;
	/** CU-14ygtkj98p8: false once no verified current IHI remains; earlier feedback stays visible. */
	requestAvailable?: boolean;
	/** CU-14ygtkj98pb: the address search needs no Verified record status (CU-14ygtkjb8x9: nor a current IHI); defaults to requestAvailable. */
	addressSearchAvailable?: boolean;
	/** CU-14ygtkj98p8: the host could not yet re-read the server IHI after a change, so its state is unknown. */
	ihiStateUnconfirmed?: boolean;
	/** CU-14ygtkjb8x9 (HI_015_005875): the server-confirmed record holds no current IHI, so the address search may assign one. */
	noCurrentIhi?: boolean;
	/** CU-14ygtkj98p8: the host re-reads the server-confirmed patient after a result that changed the current IHI. */
	onPatientRecordChanged?: () => Promise<void> | void;
}

/** CU-14ygtkj98p4 (HI_015_006105): host renders this only for a server-confirmed IHI state (CU-14ygtkjb8x9: including none held); nothing is sent until clicked. */
const IhiRevalidationPanel: React.FC<IhiRevalidationPanelProps> = ({ patientId, offer, requestAvailable = true, addressSearchAvailable = requestAvailable, ihiStateUnconfirmed = false, noCurrentIhi = false, onPatientRecordChanged }) => {
	const [feedback, setFeedback] = useState<RevalidationFeedback | null>(null);
	const [pending, setPending] = useState<"revalidation" | "address" | null>(null);
	const inFlightRef = useRef(false);

	useEffect(() => {
		setFeedback(null);
	}, [offer, patientId]);

	const handleRequest = async () => {
		if (inFlightRef.current) return;
		inFlightRef.current = true;
		setPending("revalidation");
		try {
			const response = await requestCoreDemographicIhiRevalidation(patientId);
			const result = parseCoreDemographicRevalidationResult(response);
			const recordChanged = result ? result.patientRecordChanged : responseReportsPatientRecordChanged(response);
			setFeedback(result || !recordChanged ? describeCoreDemographicRevalidationResult(result) : UNREADABLE_CHANGED_RECORD_FEEDBACK);
			if (recordChanged) await onPatientRecordChanged?.();
		} catch (error) {
			setFeedback(describeCoreDemographicRevalidationError(error));
		} finally {
			inFlightRef.current = false;
			setPending(null);
		}
	};

	// CU-14ygtkj98pb (HI_015_005815): one explicit Search Type 6 per click; an address edit never sends it.
	const handleAddressSearch = async () => {
		if (inFlightRef.current) return;
		inFlightRef.current = true;
		setPending("address");
		try {
			const response = await requestAddressIhiSearch(patientId);
			const result = parseAddressIhiSearchResult(response);
			const recordChanged = result ? result.patientRecordChanged : responseReportsPatientRecordChanged(response);
			setFeedback(result || !recordChanged ? describeAddressIhiSearchResult(result, noCurrentIhi) : UNREADABLE_CHANGED_RECORD_FEEDBACK);
			if (recordChanged) await onPatientRecordChanged?.();
		} catch (error) {
			setFeedback(describeAddressIhiSearchError(error));
		} finally {
			inFlightRef.current = false;
			setPending(null);
		}
	};

	return (
		<SectionCard title="IHI Revalidation" style={{ marginBottom: 16 }}>
			<div data-testid="ihi-revalidation-panel" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
				<HiServiceModeNotice compact />
				{requestAvailable && offer && (
					<Alert
						data-testid="core-demographic-change-notice"
						type="info"
						showIcon
						message="IHI revalidation available"
						description={`${describeChangedDemographics(offer)} changed for a patient with a verified IHI. Revalidation is not automatic: request it below to check the IHI with the HI Service.`}
					/>
				)}
				{requestAvailable ? (
					<Text type="secondary">
						This patient has a verified IHI. You can ask the HI Service to revalidate it using the patient's current saved details. It is never done automatically.
					</Text>
				) : (
					<Text type="secondary" data-testid="ihi-revalidation-unavailable">
						{ihiStateUnconfirmed
							? "The patient's current IHI could not be confirmed after this change. Refresh the patient record before requesting revalidation again."
							: noCurrentIhi
								? "This patient does not have a current IHI, so revalidation is not available. An address search can look for one to assign."
								: "This patient does not have a verified current IHI, so revalidation is not available."}
					</Text>
				)}
				{(requestAvailable || addressSearchAvailable) && (
					<div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
						{requestAvailable && (
							<Button onClick={handleRequest} loading={pending === "revalidation"} disabled={pending !== null}>
								Request IHI revalidation
							</Button>
						)}
						{addressSearchAvailable && (
							<Button onClick={handleAddressSearch} loading={pending === "address"} disabled={pending !== null}>
								Search IHI by address
							</Button>
						)}
					</div>
				)}
				{addressSearchAvailable && (
					<Text type="secondary">
						The address search uses the patient's saved name, date of birth, sex and address. It is only sent when you click it.
						{noCurrentIhi && " If it finds exactly one verified IHI, that IHI is assigned to this patient's record."}
					</Text>
				)}
				{feedback && (
					<Alert
						data-testid="ihi-revalidation-result"
						type={feedback.severity}
						showIcon
						message={feedback.title}
						description={feedback.detail}
					/>
				)}
			</div>
		</SectionCard>
	);
};

export default IhiRevalidationPanel;
